import { theme } from 'aimo-ui';
import AimoPlusIcon from 'components/account/subscription/AimoPlusIcon';
import Icon from 'components/icons/Icon';
import AccountContext from 'context/AccountContext.tsx';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { country } from 'states/persistInStorage';
import styled from 'styled-components/native';
import { CenterRow } from 'styles/ContainerStyles';
import { ParkingSession } from 'types/generatedSchemaTypes';
import { currencyFormatter } from 'utils/commonUtils';
import { getPaymentMethodDetails } from 'utils/paymentUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { getTimeFromDate, getTodayTomorrowOrDate } from 'utils/timeUtils';
import OngoingAnprParking from './OngoingAnprParking';
import {
  permitCoversParking,
  SessionPriceInfo,
} from './OngoingParkingComponents';
import { StatusContainer, StyledMeta, Time } from './styles';
import { useUiStore } from 'stores/uiStore';

type ParkingHeaderProps = {
  session: ParkingSession;
  isManual?: boolean;
};

const IconWrapper = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  max-width: 300px;
  width: 100%;
`;

const PressableArea = styled.Pressable`
  flex-direction: row;
`;

const ManualParking: React.FC<ParkingHeaderProps> = ({ session }) => {
  const { t } = useTranslation();
  const {
    customer: { paymentCards, companyBenefits },
  } = useContext(AccountContext);

  const endTime = new Date(session?.predefinedEndDate as string);
  const endDate = getTodayTomorrowOrDate(endTime);
  const parkingCountry = session.countryCode;
  const clocktime = getTimeFromDate(endTime, parkingCountry || country());
  const totalPrice = session.price?.totalAmount || 0;
  const price = currencyFormatter(totalPrice, parkingCountry || country());
  const { paymentMethodInfo, paymentMethodLogo } = getPaymentMethodDetails(
    session,
    paymentCards,
    companyBenefits
  );
  const isSessionCoveredByPermit = permitCoversParking(session);
  const hasCorporateAccount = session?.paymentType === 'CORPORATE_ACCOUNT';

  return (
    <StatusContainer>
      <StyledMeta testID="ongoing-parking-ends">
        {t('time.ends', { endDate })}
      </StyledMeta>
      <Time {...testIdentifiers('ongoing-parking-end-time')}>{clocktime}</Time>
      <IconWrapper>
        <PressableArea
          onPress={() => {
            useUiStore
              .getState()
              .setSheetModal('OngoingParkingSessionPriceBreakdownDetails');
          }}>
          <StyledMeta {...testIdentifiers('ongoing-parking-price')}>
            {price}
          </StyledMeta>
          <AimoPlusIcon hasCorporateAccount={hasCorporateAccount} />
          <Icon color={theme.colors.boost.green} name="Info-Outlined" />
        </PressableArea>
      </IconWrapper>
      <SessionPriceInfo session={session} />
      {!isSessionCoveredByPermit ? (
        <IconWrapper {...testIdentifiers('ongoing-parking-payment-method')}>
          {paymentMethodLogo && (
            <Icon
              name={`${paymentMethodLogo?.logoName}`}
              size={`${paymentMethodLogo?.logoSize}`}
              color={theme.colors.white}
            />
          )}
          <StyledMeta mb={2} ml={5}>
            {paymentMethodInfo}
          </StyledMeta>
        </IconWrapper>
      ) : (
        <CenterRow isVerticalCenter>
          <View style={{ marginTop: 2 }}>
            <Icon
              size="m"
              name="Services-Outlined"
              color={theme.colors.white}
            />
          </View>
          <StyledMeta center {...testIdentifiers('anpr-description')} ml={5}>
            {t('parking.anprCoveredByPermit')}
          </StyledMeta>
        </CenterRow>
      )}
    </StatusContainer>
  );
};

const OngoingParkingStatus = ({
  session,
  isManual = true,
}: ParkingHeaderProps) => {
  if (isManual) {
    return <ManualParking session={session} />;
  }
  return <OngoingAnprParking {...session} />;
};

// getPaymentMethodDetails moved to utils/paymentUtils (single wallet-aware
// implementation; this file used to carry an identical local copy).
export { getPaymentMethodDetails } from 'utils/paymentUtils';

export default OngoingParkingStatus;
